import {updateMyCookieConsent} from '../api/generated/endpoints/auth/auth'
import type {UserResponse} from '../api/generated/model/userResponse'
import {getConsent, hydrateFromServer, setConsent} from './store'
import type {AnalyticsConsentSource} from './store'

// Two responsibilities:
//   1. pushConsentToBackend — fire-and-forget PATCH /auth/me/cookie-consent (debounced)
//   2. reconcileWithMe — on every successful GET /auth/me, reconcile DB and LS state.
//
// localStorage is the synchronous gating source for tracker init, so we never block on the
// network here. Failures are best-effort and self-heal on the next reconcile.

const PENDING_KEY = 'ecr.cookie.consent.pending'
const SKEW_TOLERANCE_MS = 60_000
const PUSH_DEBOUNCE_MS = 500

const isBrowser = typeof window !== 'undefined'

let pushTimer: ReturnType<typeof setTimeout> | null = null
let lastQueued: {analytics: boolean; source: AnalyticsConsentSource} | null = null

const readPending = (): {analytics: boolean; source: AnalyticsConsentSource} | null => {
  if (!isBrowser) return null
  try {
    const raw = window.localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.analytics !== 'boolean') return null
    return parsed
  } catch {
    return null
  }
}

const writePending = (next: {analytics: boolean; source: AnalyticsConsentSource} | null): void => {
  if (!isBrowser) return
  try {
    if (next === null) window.localStorage.removeItem(PENDING_KEY)
    else window.localStorage.setItem(PENDING_KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
}

const sendNow = async (): Promise<void> => {
  if (!lastQueued) return
  const payload = lastQueued
  lastQueued = null
  try {
    await updateMyCookieConsent(payload)
    writePending(null)
  } catch {
    // Likely 401 (not signed in) or network failure. Queue for retry on next reconcile.
    writePending(payload)
  }
}

/**
 * Schedule a debounced push of the current consent choice to the backend. Safe to call from
 * anonymous contexts — on 401 we silently queue. Subsequent successful auth + reconcileWithMe
 * will drain the queue.
 */
const pushConsentToBackend = (analytics: boolean, source: AnalyticsConsentSource): void => {
  lastQueued = {analytics, source}
  if (pushTimer) clearTimeout(pushTimer)
  pushTimer = setTimeout(() => {
    pushTimer = null
    void sendNow()
  }, PUSH_DEBOUNCE_MS)
}

const isValidSource = (s: string | null | undefined): s is AnalyticsConsentSource =>
  s === 'banner' || s === 'cookies_page' || s === 'dnt'

/**
 * Reconcile localStorage and DB-stored consent after a fresh GET /auth/me.
 *   1. Both undecided → no-op.
 *   2. LS set, DB null → push LS → DB.
 *   3. DB set, LS missing → hydrate DB → LS.
 *   4. Both set → newest-wins with a 60s clock-skew tolerance preferring DB.
 * Also drains any queued offline write on success.
 */
const reconcileWithMe = (me: UserResponse): void => {
  const ls = getConsent()
  const dbConsent = me.analyticsConsent
  const dbUpdated = me.analyticsConsentUpdatedAt ? Date.parse(me.analyticsConsentUpdatedAt) : null
  const dbSource = isValidSource(me.analyticsConsentSource) ? me.analyticsConsentSource : null

  const pending = readPending()
  if (pending) {
    // Replay any offline write before reconciling — the user signed in / came back online.
    pushConsentToBackend(pending.analytics, pending.source)
    return
  }

  if (!ls && (dbConsent === null || dbConsent === undefined)) return

  if (ls && (dbConsent === null || dbConsent === undefined)) {
    pushConsentToBackend(ls.analytics, ls.source)
    return
  }

  if (!ls && typeof dbConsent === 'boolean' && dbUpdated && dbSource) {
    hydrateFromServer(dbConsent, dbUpdated, dbSource)
    return
  }

  if (ls && typeof dbConsent === 'boolean' && dbUpdated && dbSource) {
    const lsNewer = ls.ts > dbUpdated + SKEW_TOLERANCE_MS
    const dbNewer = dbUpdated > ls.ts + SKEW_TOLERANCE_MS
    if (lsNewer) {
      pushConsentToBackend(ls.analytics, ls.source)
    } else if (dbNewer) {
      hydrateFromServer(dbConsent, dbUpdated, dbSource)
    } else if (dbConsent !== ls.analytics) {
      // Within skew window with disagreement — prefer DB.
      hydrateFromServer(dbConsent, dbUpdated, dbSource)
    }
  }
}

/** Convenience for components: write LS synchronously + schedule BE sync. */
const writeConsent = (analytics: boolean, source: AnalyticsConsentSource): void => {
  setConsent(analytics, source)
  pushConsentToBackend(analytics, source)
}

export {pushConsentToBackend, reconcileWithMe, writeConsent}
